import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { crmStore, useCrm } from '../store';
import { Building2 } from 'lucide-react';
import type { EmetteurInfo } from '../types';

type StructureCode = 'cygnature' | 'suivez_vos_reves';

const inputCls =
  'w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50';

function Field({ label, children, className = '', hint }: { label: string; children: React.ReactNode; className?: string; hint?: string }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function BrandImageField({ label, value, placeholder, pickRef, onPick, onClear }: {
  label: string;
  value: string;
  placeholder: string;
  pickRef: React.RefObject<HTMLInputElement>;
  onPick: (file: File) => void;
  onClear: () => void;
}) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">{label}</div>
      <div className="flex items-center gap-3">
        <div className="h-14 w-28 shrink-0 rounded border border-slate-700 bg-white/90 flex items-center justify-center overflow-hidden">
          {value
            ? <img src={value} alt="" className="max-h-12 max-w-full object-contain" />
            : <span className="px-2 text-center text-[10px] text-slate-500">{placeholder}</span>}
        </div>
        <div className="flex flex-col items-start gap-1.5">
          <button type="button" onClick={() => pickRef.current?.click()} className="px-3 py-1.5 text-xs rounded border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200">
            Choisir une image
          </button>
          {!!value && (
            <button type="button" onClick={onClear} className="px-3 py-1 text-[11px] text-slate-400 hover:text-red-300">
              Retirer
            </button>
          )}
        </div>
      </div>
      <input
        ref={pickRef}
        type="file"
        accept="image/png,image/jpeg"
        hidden
        onChange={(ev) => {
          const f = ev.target.files?.[0];
          if (f) onPick(f);
          ev.target.value = '';
        }}
      />
    </div>
  );
}

export function EmetteurEditor({ onClose, initialStructure = 'cygnature' }: { onClose: () => void; initialStructure?: StructureCode }) {
  const data = useCrm();
  const [structure, setStructure] = useState<StructureCode>(initialStructure);
  const map = ((data as any).emetteurs || { cygnature: data.emetteur }) as Record<string, EmetteurInfo>;
  const [e, setE] = useState<EmetteurInfo>(map[structure] || data.emetteur);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const tamponRef = useRef<HTMLInputElement>(null);

  const readBrandImage = (file: File, key: 'logoUrl' | 'tamponUrl') => {
    if (!file.type.startsWith('image/')) { toast.error('Choisissez une image PNG ou JPG'); return; }
    if (file.size > 400 * 1024) { toast.error('Image trop lourde : 400 Ko maximum'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const v = String(reader.result);
      setE((prev) => (key === 'logoUrl' ? { ...prev, logoUrl: v } : { ...prev, tamponUrl: v }));
    };
    reader.onerror = () => toast.error('Lecture de l’image impossible');
    reader.readAsDataURL(file);
  };

  const switchStructure = (s: StructureCode) => {
    setStructure(s);
    setE(map[s] || data.emetteur);
  };

  const save = async () => {
    setSaving(true);
    try {
      await crmStore.updateEmetteur(e, structure);
      toast.success(`Émetteur « ${structure === 'cygnature' ? 'Cygnature Production' : 'Suivez Vos Rêves Event'} » enregistré`);
      onClose();
    } catch (err: any) {
      toast.error(`Erreur : ${err?.message || 'enregistrement impossible'}`);
    } finally {
      setSaving(false);
    }
  };

  const missing = !e.raisonSociale || !e.adresse || !e.siret || !e.tvaIntra || !e.iban || !e.bic;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-3xl my-8">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900 rounded-t-xl">
          <h2 className="text-lg font-heading text-slate-100 flex items-center gap-3">
            <Building2 className="text-amber-400" size={20} strokeWidth={1.5} />
            Émetteur / Coordonnées légales
          </h2>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200">Annuler</button>
            <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-md text-sm font-medium disabled:opacity-50">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>

        <div className="px-6 pt-4">
          <div className="inline-flex rounded-md border border-slate-800 bg-slate-950/40 p-1">
            {(['cygnature', 'suivez_vos_reves'] as StructureCode[]).map((s) => (
              <button
                key={s}
                onClick={() => switchStructure(s)}
                className={`px-3 py-1.5 text-xs rounded ${structure === s ? 'bg-amber-500/20 text-amber-200' : 'text-slate-400 hover:text-slate-200'}`}
              >
                {s === 'cygnature' ? 'Cygnature Production (SASU)' : 'Suivez Vos Rêves Event (Asso)'}
              </button>
            ))}
          </div>
          {missing && (
            <div className="mt-3 px-3 py-2 rounded-md border border-red-500/30 bg-red-500/10 text-xs text-red-300">
              Champs obligatoires manquants — les factures afficheront « [Information à insérer] » tant qu'ils ne sont pas remplis.
            </div>
          )}
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Raison sociale *" className="md:col-span-2">
            <input value={e.raisonSociale} onChange={(ev) => setE({ ...e, raisonSociale: ev.target.value })} className={inputCls} />
          </Field>
          <Field label="Adresse complète *" className="md:col-span-2">
            <textarea rows={2} value={e.adresse} onChange={(ev) => setE({ ...e, adresse: ev.target.value })} className={inputCls} />
          </Field>
          <Field label="SIRET *"><input value={e.siret} onChange={(ev) => setE({ ...e, siret: ev.target.value })} placeholder="14 chiffres" className={inputCls} /></Field>
          <Field label="N° TVA intracommunautaire *"><input value={e.tvaIntra} onChange={(ev) => setE({ ...e, tvaIntra: ev.target.value })} placeholder="FR…" className={inputCls} /></Field>
          <Field label="Code APE / NAF"><input value={e.ape} onChange={(ev) => setE({ ...e, ape: ev.target.value })} placeholder="Ex. 9001Z" className={inputCls} /></Field>
          <Field label="RCS"><input value={e.rcs} onChange={(ev) => setE({ ...e, rcs: ev.target.value })} placeholder="Ex. Reims B 898 473 962" className={inputCls} /></Field>
          <Field label="Capital social"><input value={e.capital} onChange={(ev) => setE({ ...e, capital: ev.target.value })} placeholder="Ex. 1 000 €" className={inputCls} /></Field>
          <Field label="Email"><input value={e.email} onChange={(ev) => setE({ ...e, email: ev.target.value })} className={inputCls} /></Field>
          <Field label="Téléphone"><input value={e.tel} onChange={(ev) => setE({ ...e, tel: ev.target.value })} className={inputCls} /></Field>
          <Field label="IBAN *"><input value={e.iban} onChange={(ev) => setE({ ...e, iban: ev.target.value })} className={inputCls} /></Field>
          <Field label="BIC *"><input value={e.bic} onChange={(ev) => setE({ ...e, bic: ev.target.value })} className={inputCls} /></Field>

          <div className="md:col-span-2 mt-2 pt-3 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Apparence des documents</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <BrandImageField
                label="Logo (devis, factures, contrats)"
                value={e.logoUrl || ''}
                placeholder="Logo par défaut (CYGNATURE)"
                pickRef={logoRef}
                onPick={(file) => readBrandImage(file, 'logoUrl')}
                onClear={() => setE({ ...e, logoUrl: '' })}
              />
              <BrandImageField
                label="Tampon / signature (contrats)"
                value={e.tamponUrl || ''}
                placeholder="Tampon par défaut"
                pickRef={tamponRef}
                onPick={(file) => readBrandImage(file, 'tamponUrl')}
                onClear={() => setE({ ...e, tamponUrl: '' })}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Images PNG ou JPG de 400 Ko maximum. Sans image enregistrée, le logo et le tampon habituels sont conservés.
            </p>
          </div>

          <div className="md:col-span-2 mt-2 pt-3 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Numérotation</div>
          </div>
          <Field label="Préfixe factures"><input value={e.prefixFacture} onChange={(ev) => setE({ ...e, prefixFacture: ev.target.value })} className={inputCls} /></Field>
          <Field label="Prochain n° facture"><input type="number" value={e.prochainNumero} onChange={(ev) => setE({ ...e, prochainNumero: parseInt(ev.target.value) || 1 })} className={inputCls} /></Field>
          <Field label="Préfixe devis"><input value={e.prefixDevis || ''} onChange={(ev) => setE({ ...e, prefixDevis: ev.target.value })} className={inputCls} /></Field>
          <Field label="Prochain n° devis"><input type="number" value={e.prochainNumeroDevis || 1} onChange={(ev) => setE({ ...e, prochainNumeroDevis: parseInt(ev.target.value) || 1 })} className={inputCls} /></Field>

          <div className="md:col-span-2 mt-2 pt-3 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Facturation électronique 2026</div>
          </div>
          <Field label="Plateforme de Dématérialisation Partenaire (PDP)" className="md:col-span-2" hint="Ex. Sage Network, Pennylane PDP, Docaposte…">
            <input value={e.plateformeAgree || ''} onChange={(ev) => setE({ ...e, plateformeAgree: ev.target.value })} className={inputCls} />
          </Field>
        </div>
      </div>
    </div>
  );
}

export default EmetteurEditor;
